import { useMemo } from "react"

import {
  OrgSettingsDetailQuery,
  UserRef,
  usePortfolioUsersQuery,
} from "@/v2/graphql/public-generated-rq"
import { DataTable } from "@lumen-energy/design-system"
import { createColumnHelper } from "@tanstack/react-table"

import { Loader } from "../../Loader"
import { Setting } from "../Setting"
import { InviteUserModal } from "./InviteUserModal"

export const UserManagement = ({
  org,
  portfolios,
}: {
  org: OrgSettingsDetailQuery["currentUser"]["org"]
  portfolios: OrgSettingsDetailQuery["portfolios"]
}) => {
  if (org?.externalFinancier) {
    return <UsersTable users={org.users} entityName={org.name} portfolioPid={null} />
  }
  return (
    <div className="space-y-4">
      {portfolios.map((portfolio) => (
        <PortfolioUsersTable key={portfolio.pid} portfolioPid={portfolio.pid} />
      ))}
    </div>
  )
}

const UsersTable = ({
  users,
  entityName,
  portfolioPid,
}: {
  users: UserRef[]
  entityName: string
  portfolioPid: string | null
}) => {
  const columns = useMemo(() => {
    const columnHelper = createColumnHelper<UserRef>()
    return [
      columnHelper.accessor("name", { header: "Name" }),
      columnHelper.accessor("email", { header: "Email" }),
    ]
  }, [])
  return (
    <Setting
      label="Users"
      description={`Email your coworkers to invite them to ${entityName} on Lumen Energy.`}
      actionButton={<InviteUserModal {...{ entityName, portfolioPid }} />}
    >
      <DataTable
        id="user-management-table"
        data={users}
        columns={columns}
        enableColumnVisibility={false}
        enableFullscreenToggle={false}
      />
    </Setting>
  )
}

const PortfolioUsersTable = ({ portfolioPid }: { portfolioPid: string }) => {
  const result = usePortfolioUsersQuery({ portfolioPid })
  return (
    <Loader
      result={result}
      render={({ data }) => (
        <UsersTable
          users={data?.portfolio?.users ?? []}
          entityName={data?.portfolio?.title ?? "Your portfolio"}
          portfolioPid={portfolioPid}
        />
      )}
    />
  )
}
